import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, ref, watch } from 'vue'

import ReportChart from '@/components/reports/ReportChart.vue'
import { DATE_RANGE_OPTIONS, dateRangeForApi, formatDateRangeLabel } from '@/helpers/reportDateRanges'
import {
  parseCustomerGrowthByTierReport,
  parseDiscountCodeUsageReport,
  parseMealPlansReport,
  parseMembershipTierGrowthReport,
  parseOrdersReport,
  parsePayoutsReport,
  parseProductsSoldBreakdownReport,
  parseRecurringMembershipFeesReport,
  parseReferralCodeUsageReport,
  parseRevenueByMembershipTierReport,
  parseRevenueReport
} from '@/services/reportParser.service'
import api from '@/store/api'


export default {
  __name: 'DynamicReport',
  props: {
  reportId: { type: String, required: true },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  dateFilter: { type: Boolean, default: false },
  minDate: { type: String, default: undefined },
  maxDate: { type: String, default: undefined },
  dropdownFilter: { type: Boolean, default: false },
  dropdownOptions: { type: Array, default: () => [] },
  hasSupportingTable: { type: Boolean, default: false },
  dataSource: { type: Object, default: null },
  chartType: { type: String, default: 'line' },
  dropdownFilter2: { type: Boolean, default: false },
  dropdown2Label: { type: String, default: null },
  dropdownFilter3: { type: Boolean, default: false },
  dropdown3Label: { type: String, default: 'All products' },
  initialMaxProducts: { type: Number, default: undefined },
  initialSelectedIds: { type: Array, default: () => [] },
  membershipTierIds: { type: Array, default: () => [] }
},
  emits: ['report-date-range'],
  setup(__props, { emit: __emit }) {

const emit = __emit
const props = __props

const PRODUCT_SALES_REPORT_ENDPOINTS = {
  products_sold_breakdown: 'products_sold_breakdown_report',
  upsell_sales: 'upsell_sales_report'
}
const PRODUCT_SALES_REPORT_TYPES = Object.keys(PRODUCT_SALES_REPORT_ENDPOINTS)
const STORE_FILTERED_REPORT_TYPES = [
  ...PRODUCT_SALES_REPORT_TYPES,
  'revenue',
  'discount_code_usage',
  'referral_code_usage',
  'customer_growth_by_tier',
  'membership_tier_growth',
  'revenue_by_membership_tier'
]
const STALE_RESPONSE = Symbol('stale response')

const selectedDateRange = ref('30d')
const dateRangeValue = ref(null)
const selectedDateRangeLabel = ref('')
const selectedDropdown = ref('all')
const selectedStore = ref([])
const selectedProductIds = ref([])
const userHasClearedProducts = ref(false)
const hasInitializedDropdown2 = ref(false)
const hasInitializedDropdown3 = ref(false)
const activeInChartProductIds = ref([])
const storesOptions = ref([])
const fulfillmentMethodOptions = ref([])
const productsOptions = ref([])
const tooltipData = ref(null)
const loading = ref(false)
const chartData = ref({ labels: [], datasets: [] })
const tableData = ref(null)

let reportRun = 0
let latestFetch = 0

const dateRangeOptions = ref(DATE_RANGE_OPTIONS)

const storeDropdownOptions = computed(() => {
  return (storesOptions.value ?? []).map(s => ({
    label: s.attributes?.store_name ?? s.store_name ?? String(s.id),
    value: String(s.id)
  }))
})

const dropdownOptions2 = computed(() => {
  if (props.dataSource?.report_type === 'orders') {
    return (fulfillmentMethodOptions.value ?? []).map(fm => ({
      label: fm.name ?? `Fulfillment ${fm.id}`,
      value: String(fm.id)
    }))
  }
  return storeDropdownOptions.value
})

const productDropdownOptions = computed(() => {
  const list = (productsOptions.value ?? []).map(p => ({
    label: p.product_name ?? `Product ${p.id}`,
    value: p.id
  }))
  return list.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? '', undefined, { sensitivity: 'base' }))
})

const legendOptionsForChart = computed(() => {
  if (props.dataSource?.report_type === 'orders') {
    return (fulfillmentMethodOptions.value ?? []).map(fm => ({
      label: fm.name ?? `Fulfillment ${fm.id}`,
      value: fm.id
    }))
  }
  return productDropdownOptions.value
})

function getDateRangeForApi() {
  return dateRangeForApi(selectedDateRange.value, dateRangeValue.value)
}

async function fetchReportData() {
  const fetchId = ++latestFetch
  const get = path =>
    api.call('GET', path).then(res => {
      if (fetchId !== latestFetch) throw STALE_RESPONSE
      return res
    })
  loading.value = true
  try {
    const ds = props.dataSource
    if (ds?.type === 'data_download' && ds?.report_type === 'payouts') {
      const range = getDateRangeForApi()
      const res = await get(
        `data_downloads/reports/payout_summary_report?beginning_date=${encodeURIComponent(range.beginning_date)}&ending_date=${encodeURIComponent(range.ending_date)}`
      )
      const payouts = res?.data?.data ?? []
      const groupBy = selectedDropdown.value || 'day'
      const parsed = parsePayoutsReport(payouts, groupBy)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      emit('report-date-range', range)
      return
    }
    if (ds?.type === 'data_download' && ds?.report_type === 'meal_plans') {
      const range = getDateRangeForApi()
      const res = await get(
        `data_downloads/reports/meal_plans_report?beginning_date=${encodeURIComponent(range.beginning_date)}&ending_date=${encodeURIComponent(range.ending_date)}`
      )
      const data = res?.data?.data ?? {}
      const groupBy = selectedDropdown.value || 'day'
      const parsed = parseMealPlansReport(data, groupBy)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      emit('report-date-range', range)
      return
    }
    if (ds?.type === 'data_download' && ds?.report_type === 'recurring_membership_fees') {
      const range = getDateRangeForApi()
      const res = await get(
        `data_downloads/reports/recurring_membership_fees_report?beginning_date=${encodeURIComponent(range.beginning_date)}&ending_date=${encodeURIComponent(range.ending_date)}`
      )
      const rows = res?.data?.data ?? []
      const groupBy = selectedDropdown.value || 'day'
      const parsed = parseRecurringMembershipFeesReport(rows, groupBy)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      emit('report-date-range', range)
      return
    }
    if (ds?.type === 'data_download' && ds?.report_type === 'revenue') {
      const range = getDateRangeForApi()
      const groupBy = selectedDropdown.value || 'day'
      const storeIds = Array.isArray(selectedStore.value) ? selectedStore.value : []
      const params = new URLSearchParams({
        beginning_date: range.beginning_date,
        ending_date: range.ending_date
      })
      storeIds.forEach(id => params.append('store_ids[]', id))
      const res = await get(`data_downloads/reports/revenue_report?${params.toString()}`)
      const rows = res?.data?.data ?? []
      const parsed = parseRevenueReport(rows, groupBy)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      emit('report-date-range', { ...range, store_ids: storeIds, group_by: groupBy })
      return
    }
    if (ds?.type === 'data_download' && ds?.report_type === 'orders') {
      const range = getDateRangeForApi()
      const groupBy = selectedDropdown.value || 'day'
      const fulfillmentMethodIds = Array.isArray(selectedStore.value) ? selectedStore.value : []
      const params = new URLSearchParams({
        beginning_date: range.beginning_date,
        ending_date: range.ending_date,
        group_by: groupBy
      })
      fulfillmentMethodIds.forEach(id => params.append('fulfillment_method_ids[]', id))
      const res = await get(`data_downloads/reports/orders_report?${params.toString()}`)
      const parsed = parseOrdersReport(res)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      tooltipData.value = parsed.tooltipData
      const payload = res?.data?.data
      const data = payload?.attributes ?? payload ?? res?.data ?? {}
      fulfillmentMethodOptions.value = data.fulfillment_method_options ?? []
      // Auto-select all fulfillment methods on initial load
      if (!hasInitializedDropdown2.value && fulfillmentMethodOptions.value.length > 0) {
        selectedStore.value = fulfillmentMethodOptions.value.map(fm => String(fm.id))
        hasInitializedDropdown2.value = true
      }
      const rows = parsed.tableData?.rows ?? []
      const allRowFmIds = rows.map(r => r.fulfillmentMethodId).filter(id => id != null)
      const topN = props.initialMaxProducts ?? 5
      activeInChartProductIds.value = allRowFmIds.slice(0, topN)
      emit('report-date-range', { ...range, fulfillment_method_ids: fulfillmentMethodIds })
      return
    }
    if (ds?.type === 'data_download' && PRODUCT_SALES_REPORT_ENDPOINTS[ds?.report_type]) {
      const range = getDateRangeForApi()
      const groupBy = selectedDropdown.value || 'day'
      const storeIds = Array.isArray(selectedStore.value) ? selectedStore.value : []
      const productIds = (Array.isArray(selectedProductIds.value) ? selectedProductIds.value : [])
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0)
      const params = new URLSearchParams({
        beginning_date: range.beginning_date,
        ending_date: range.ending_date,
        group_by: groupBy
      })
      storeIds.forEach(id => params.append('store_ids[]', id))
      productIds.forEach(id => params.append('product_ids[]', id))
      const res = await get(
        `data_downloads/reports/${PRODUCT_SALES_REPORT_ENDPOINTS[ds.report_type]}?${params.toString()}`
      )
      const parsed = parseProductsSoldBreakdownReport(res)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      tooltipData.value = parsed.tooltipData
      productsOptions.value = parsed.productsOptions ?? []
      if (!hasInitializedDropdown3.value && productsOptions.value.length > 0) {
        selectedProductIds.value = productsOptions.value.map(p => p.id)
        hasInitializedDropdown3.value = true
      }
      const rows = parsed.tableData?.rows ?? []
      const allRowProductIds = rows.map(r => r.productId).filter(id => id != null)
      if (productIds.length > 0) {
        activeInChartProductIds.value = productIds
      } else if (userHasClearedProducts.value) {
        activeInChartProductIds.value = [...allRowProductIds]
      } else {
        const topN = props.initialMaxProducts ?? 5
        activeInChartProductIds.value = allRowProductIds.slice(0, topN)
      }
      emit('report-date-range', { ...range, store_ids: storeIds, product_ids: productIds })
      return
    }
    if (ds?.type === 'data_download' && ds?.report_type === 'discount_code_usage') {
      const range = getDateRangeForApi()
      const groupBy = selectedDropdown.value || 'day'
      const storeIds = Array.isArray(selectedStore.value) ? selectedStore.value : []
      const discountCodeIds = (Array.isArray(selectedProductIds.value) ? selectedProductIds.value : [])
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0)
      const params = new URLSearchParams({
        beginning_date: range.beginning_date,
        ending_date: range.ending_date,
        group_by: groupBy
      })
      storeIds.forEach(id => params.append('store_ids[]', id))
      discountCodeIds.forEach(id => params.append('discount_code_ids[]', id))
      const res = await get(`data_downloads/reports/discount_code_usage_report?${params.toString()}`)
      const parsed = parseDiscountCodeUsageReport(res)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      tooltipData.value = parsed.tooltipData
      productsOptions.value = parsed.productsOptions ?? []
      if (!hasInitializedDropdown3.value && productsOptions.value.length > 0) {
        selectedProductIds.value = productsOptions.value.map(p => p.id)
        hasInitializedDropdown3.value = true
      }
      const rows = parsed.tableData?.rows ?? []
      const allRowCodeIds = rows.map(r => r.productId).filter(id => id != null)
      if (discountCodeIds.length > 0) {
        activeInChartProductIds.value = discountCodeIds
      } else if (userHasClearedProducts.value) {
        activeInChartProductIds.value = [...allRowCodeIds]
      } else {
        const topN = props.initialMaxProducts ?? 5
        activeInChartProductIds.value = allRowCodeIds.slice(0, topN)
      }
      emit('report-date-range', { ...range, store_ids: storeIds, discount_code_ids: discountCodeIds })
      return
    }
    if (ds?.type === 'data_download' && ds?.report_type === 'referral_code_usage') {
      const range = getDateRangeForApi()
      const groupBy = selectedDropdown.value || 'day'
      const storeIds = Array.isArray(selectedStore.value) ? selectedStore.value : []
      const referralCodeIds = (Array.isArray(selectedProductIds.value) ? selectedProductIds.value : [])
        .map(id => Number(id))
        .filter(id => Number.isFinite(id) && id > 0)
      const params = new URLSearchParams({
        beginning_date: range.beginning_date,
        ending_date: range.ending_date,
        group_by: groupBy
      })
      storeIds.forEach(id => params.append('store_ids[]', id))
      referralCodeIds.forEach(id => params.append('referral_code_ids[]', id))
      const res = await get(`data_downloads/reports/referral_code_usage_report?${params.toString()}`)
      const parsed = parseReferralCodeUsageReport(res)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      tooltipData.value = parsed.tooltipData
      productsOptions.value = parsed.productsOptions ?? []
      if (!hasInitializedDropdown3.value && productsOptions.value.length > 0) {
        selectedProductIds.value = productsOptions.value.map(p => p.id)
        hasInitializedDropdown3.value = true
      }
      const rows = parsed.tableData?.rows ?? []
      const allRowCodeIds = rows.map(r => r.productId).filter(id => id != null)
      if (referralCodeIds.length > 0) {
        activeInChartProductIds.value = referralCodeIds
      } else if (userHasClearedProducts.value) {
        activeInChartProductIds.value = [...allRowCodeIds]
      } else {
        const topN = props.initialMaxProducts ?? 5
        activeInChartProductIds.value = allRowCodeIds.slice(0, topN)
      }
      emit('report-date-range', { ...range, store_ids: storeIds, referral_code_ids: referralCodeIds })
      return
    }
    if (ds?.type === 'data_download' && ds?.report_type === 'customer_growth_by_tier') {
      const range = getDateRangeForApi()
      const groupBy = selectedDropdown.value || 'day'
      const storeIds = Array.isArray(selectedStore.value) ? selectedStore.value : []
      const params = new URLSearchParams({
        beginning_date: range.beginning_date,
        ending_date: range.ending_date,
        group_by: groupBy
      })
      storeIds.forEach(id => params.append('store_ids[]', id))
      const res = await get(`data_downloads/reports/customer_growth_by_tier_report?${params.toString()}`)
      const parsed = parseCustomerGrowthByTierReport(res)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      emit('report-date-range', { ...range, store_ids: storeIds })
      return
    }
    if (ds?.type === 'data_download' && ds?.report_type === 'membership_tier_growth') {
      const range = getDateRangeForApi()
      const groupBy = selectedDropdown.value || 'day'
      const storeIds = Array.isArray(selectedStore.value) ? selectedStore.value : []
      const params = new URLSearchParams({
        beginning_date: range.beginning_date,
        ending_date: range.ending_date,
        group_by: groupBy
      })
      storeIds.forEach(id => params.append('store_ids[]', id))
      props.membershipTierIds.forEach(id => params.append('membership_tier_ids[]', id))
      const res = await get(`data_downloads/reports/membership_tier_growth_report?${params.toString()}`)
      const parsed = parseMembershipTierGrowthReport(res)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      emit('report-date-range', { ...range, store_ids: storeIds })
      return
    }
    if (ds?.type === 'data_download' && ds?.report_type === 'revenue_by_membership_tier') {
      const range = getDateRangeForApi()
      const groupBy = selectedDropdown.value || 'day'
      const storeIds = Array.isArray(selectedStore.value) ? selectedStore.value : []
      const membershipTierIds = Array.isArray(selectedProductIds.value) ? selectedProductIds.value : []
      const params = new URLSearchParams({
        beginning_date: range.beginning_date,
        ending_date: range.ending_date,
        group_by: groupBy
      })
      storeIds.forEach(id => params.append('store_ids[]', id))
      membershipTierIds.forEach(id => params.append('membership_tier_ids[]', id))
      const res = await get(`data_downloads/reports/revenue_by_membership_tier_report?${params.toString()}`)
      const parsed = parseRevenueByMembershipTierReport(res)
      chartData.value = parsed.chartData
      tableData.value = parsed.tableData
      emit('report-date-range', {
        ...range,
        group_by: groupBy,
        store_ids: storeIds,
        membership_tier_ids: membershipTierIds
      })
      return
    }
    chartData.value = { labels: [], datasets: [] }
    tableData.value = null
    tooltipData.value = null
  } catch (error) {
    if (error !== STALE_RESPONSE) throw error
  } finally {
    if (fetchId === latestFetch) loading.value = false
  }
}

function resetToTop5Products() {
  if (
    PRODUCT_SALES_REPORT_TYPES.includes(props.dataSource?.report_type) ||
    props.dataSource?.report_type === 'discount_code_usage' ||
    props.dataSource?.report_type === 'referral_code_usage'
  ) {
    userHasClearedProducts.value = false
    selectedProductIds.value = []
  }
}

function onDateRangeChange(value) {
  if (typeof value === 'string') {
    selectedDateRange.value = value
    dateRangeValue.value = null
    selectedDateRangeLabel.value = ''
  } else if (value && typeof value === 'object' && value.start != null && value.end != null) {
    dateRangeValue.value = value
    selectedDateRangeLabel.value = formatDateRangeLabel(value.start, value.end)
    selectedDateRange.value = ''
  }
  resetToTop5Products()
  fetchReportData()
}

function onDropdownChange(value) {
  selectedDropdown.value = value
  resetToTop5Products()
  fetchReportData()
}

function onDropdownChange2(value) {
  selectedStore.value = Array.isArray(value) ? value : value != null ? [value] : []
  resetToTop5Products()
  fetchReportData()
}

function onDropdownChange3(value) {
  const nextIds = Array.isArray(value) ? value : value != null ? [value] : []
  if (nextIds.length === 0) userHasClearedProducts.value = true
  else userHasClearedProducts.value = false
  selectedProductIds.value = nextIds
  fetchReportData()
}

// "Show in chart" only changes what is drawn. The spreadsheet download keeps
// following the date range and dropdown filters, so hiding a series (the chart
// starts with the top five) never trims the report (BOT-1661).
function onActiveChartProductsChange(ids) {
  activeInChartProductIds.value = Array.isArray(ids) ? ids : []
}

async function fetchStores() {
  try {
    const res = await api.call('GET', 'stores?included=none')
    const data = res?.data?.data ?? res?.data ?? []
    storesOptions.value = Array.isArray(data) ? data : data ? [data] : []
  } catch {
    storesOptions.value = []
  }
}

function resetReportState() {
  latestFetch++
  selectedDateRange.value = '30d'
  dateRangeValue.value = null
  selectedDateRangeLabel.value = ''
  selectedDropdown.value = props.dropdownOptions?.[0]?.value ?? 'all'
  selectedStore.value = []
  selectedProductIds.value = [...props.initialSelectedIds]
  userHasClearedProducts.value = false
  hasInitializedDropdown2.value = false
  hasInitializedDropdown3.value = props.initialSelectedIds.length > 0
  activeInChartProductIds.value = []
  storesOptions.value = []
  fulfillmentMethodOptions.value = []
  productsOptions.value = []
  tooltipData.value = null
  chartData.value = { labels: [], datasets: [] }
  tableData.value = null
}

async function loadReport() {
  const run = ++reportRun
  resetReportState()
  if (props.dropdownFilter2 && STORE_FILTERED_REPORT_TYPES.includes(props.dataSource?.report_type)) {
    loading.value = true
    await fetchStores()
    if (run !== reportRun) return
    // Pre-select all stores so checkboxes start checked
    selectedStore.value = storeDropdownOptions.value.map(s => s.value)
    hasInitializedDropdown2.value = true
  }
  fetchReportData()
}

loadReport()

watch(() => `${props.reportId}:${props.initialSelectedIds.join(',')}`, loadReport)

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (_openBlock(), _createBlock(ReportChart, {
      key: __props.reportId,
      "report-id": __props.reportId,
      title: __props.title,
      description: __props.description,
      "chart-data": chartData.value,
      "date-filter": __props.dateFilter,
      "min-date": __props.minDate,
      "max-date": __props.maxDate,
      "selected-date-range": selectedDateRange.value,
      "selected-date-range-label": selectedDateRangeLabel.value,
      "custom-date-range": dateRangeValue.value,
      "date-range-options": dateRangeOptions.value,
      "dropdown-filter": __props.dropdownFilter,
      "dropdown-options": __props.dropdownOptions,
      "selected-dropdown": selectedDropdown.value,
      "dropdown-filter2": __props.dropdownFilter2,
      "dropdown2-label": __props.dropdown2Label,
      "dropdown-options2": dropdownOptions2.value,
      "selected-dropdown2": selectedStore.value,
      "dropdown-filter3": __props.dropdownFilter3,
      "dropdown3-label": __props.dropdown3Label,
      "dropdown-options3": legendOptionsForChart.value,
      "selected-dropdown3": selectedProductIds.value,
      "has-supporting-table": __props.hasSupportingTable,
      "table-data": tableData.value,
      loading: loading.value,
      "chart-type": __props.chartType,
      "tooltip-data": tooltipData.value,
      "table-hover-enabled": true,
      "active-in-chart-product-ids": 
        [..._unref(PRODUCT_SALES_REPORT_TYPES), 'orders', 'discount_code_usage', 'referral_code_usage'].includes(
          __props.dataSource?.report_type
        )
          ? activeInChartProductIds.value
          : null
      ,
      onDateRangeChange: onDateRangeChange,
      onDropdownChange: onDropdownChange,
      onDropdownChange2: onDropdownChange2,
      onDropdownChange3: onDropdownChange3,
      onActiveChartProductsChange: onActiveChartProductsChange
    }, null, 8, ["report-id", "title", "description", "chart-data", "date-filter", "min-date", "max-date", "selected-date-range", "selected-date-range-label", "custom-date-range", "date-range-options", "dropdown-filter", "dropdown-options", "selected-dropdown", "dropdown-filter2", "dropdown2-label", "dropdown-options2", "selected-dropdown2", "dropdown-filter3", "dropdown3-label", "dropdown-options3", "selected-dropdown3", "has-supporting-table", "table-data", "loading", "chart-type", "tooltip-data", "active-in-chart-product-ids"]))
  ]))
}
}

}